import React, { DragEvent, useEffect, useRef, useState } from "react";
import Nouislider from "nouislider-react";
import "nouislider/distribute/nouislider.css";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import {
  type FieldError,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import classNames from "classnames";
import { CloseSvg } from "../../../icons";
import { formatBytes, getNestedError } from "../../../utils/helpers";
import Button from "../button";

const ffmpeg = new FFmpeg();

type IFileDropZoneProps = {
  label?: string;
  onSelectOrDrop?: (files: FileList) => void;
  onClear?: () => void;
  accept?: string;
  name?: string;
  rules?: Omit<
    RegisterOptions,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  resetFile?: boolean;
  onChange?: (file: File | null) => void;
  maxDuration?: number;
};

type IVideoTrimmerProps = {
  videoSrc: string;
  isOpen: boolean;
  closeModal: () => void;
  isScriptLoaded: boolean;
  videoFileValue: File | null;
  startTime: number;
  endTime: number;
  setStartTime: (time: number) => void;
  setEndTime: (time: number) => void;
  videoDuration: number;
  setVideoTrimmedUrl: (url: string) => void;
  handleClearFile: (e: React.MouseEvent) => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  videoTrimmedUrl: string;
  FieldOnChange?: (file: File | null) => void;
  onChange?: (file: File | null) => void;
  maxDuration?: number;
};

const VideoTrimmer: React.FC<IVideoTrimmerProps> = ({
  videoSrc,
  closeModal,
  isScriptLoaded,
  videoFileValue,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  videoDuration,
  setVideoTrimmedUrl,
  videoRef,
  videoTrimmedUrl,
  FieldOnChange,
  onChange,
  maxDuration = 10,
}) => {
  const sliderRef = useRef<any>(null);
  const [trimLoading, setTrimLoading] = useState<boolean>(false);
  const [maxDurationError, setMaxDurationError] = useState<string>("");
  useEffect(() => {
    const roundedStartTime = Math.floor(startTime);
    const roundedDuration = Math.floor(videoDuration);
    const newEndTime = Math.min(
      roundedDuration,
      roundedStartTime + maxDuration
    );

    setEndTime(newEndTime);

    if (sliderRef.current) {
      sliderRef.current.noUiSlider.set([roundedStartTime, newEndTime]);
    }
  }, [videoDuration, startTime, setEndTime]);

  const handleSliderUpdate = (values: (string | number)[]) => {
    let [newStart, newEnd] = values.map((val) => Math.floor(Number(val)));
    const duration = newEnd - newStart;

    // Get previous slider values (to detect which handle moved)
    const [prevStart, prevEnd] =
      (sliderRef.current?.noUiSlider.get() as number[]) || [startTime, endTime];

    // Check which handle moved more (start or end)
    const isEndHandleMoved =
      Math.abs(newEnd - prevEnd) > Math.abs(newStart - prevStart);

    if (duration > maxDuration) {
      if (isEndHandleMoved) {
        // User dragged the end handle → adjust start
        newStart = Math.max(newEnd - maxDuration, 0);
      } else {
        // User dragged the start handle → adjust end
        newEnd = Math.min(newStart + maxDuration, videoDuration);
      }
    }

    // Ensure start < end and within bounds
    newStart = Math.max(0, Math.min(newStart, videoDuration - 1));
    newEnd = Math.min(Math.max(newEnd, newStart + 1), videoDuration);

    // Update state & slider
    setStartTime(newStart);
    setEndTime(newEnd);

    if (sliderRef.current) {
      sliderRef.current.noUiSlider.set([newStart, newEnd], false); // `false` prevents infinite loop
    }
  };

  const handleTrim = async () => {
    if (isScriptLoaded && videoFileValue) {
      const selectedDuration = endTime - startTime;
      if (selectedDuration > maxDuration) {
        setMaxDurationError(
          `Trim duration cannot exceed ${maxDuration} seconds.`
        );
        return; // Exit early if invalid
      }
      try {
        setTrimLoading(true);

        const videoArrayBuffer = await videoFileValue.arrayBuffer();
        const videoUint8Array = new Uint8Array(videoArrayBuffer);

        await ffmpeg.writeFile("input.mp4", videoUint8Array);
        await ffmpeg.exec([
          "-ss",
          startTime.toString(),
          "-i",
          "input.mp4",
          "-t",
          (endTime - startTime).toString(),
          "-preset",
          "ultrafast",
          "-vf",
          "scale=-2:1080,fps=25",
          "-c:v",
          "libx264",
          "-c:a",
          "aac",
          "-movflags",
          "+faststart",
          "output.mp4",
        ]);

        const data = await ffmpeg.readFile("output.mp4");
        const uint8Array = data instanceof Uint8Array ? new Uint8Array(data) : new Uint8Array();
        const blob = new Blob([uint8Array], { type: "video/mp4" });
        const url = URL.createObjectURL(blob);
        setVideoTrimmedUrl(url);

        const trimmedFileRef = new File([blob], videoFileValue.name, {
          type: "video/mp4",
          lastModified: Date.now(),
        });
        FieldOnChange?.(trimmedFileRef);
        onChange?.(trimmedFileRef);
      } finally {
        setTrimLoading(false);
      }
    }
  };

  const handleClose = () => {
    closeModal();
  };

  return (
    <div className="mt-2">
      {videoSrc && (
        <>
          <div className="p-2 border-gray-400 border-[0.86px] border-dashed rounded">
            {videoFileValue && (
              <div className="flex items-center gap-5 text-sm leading-[18.03px] font-normal text-[#666672]">
                <div className="flex flex-col w-100 font-bold">
                  <span>Selected File: {videoFileValue?.name}</span>
                  <span>
                    File Size:{formatBytes(videoFileValue?.size || 0)}
                  </span>
                </div>
              </div>
            )}
            <div
              className=""
              style={{ width: "250px", margin: "0 auto", marginTop: "10px" }}
            >
              <video
                ref={videoRef}
                src={videoSrc}
                controls
                className="max-w-xl rounded-lg mt-1 w-full"
              />
              <style>
                {`
                  .noUi-horizontal {
                    height: 10px;
                  }
                  .noUi-horizontal .noUi-handle {
                    width: 20px;
                    height: 20px;
                    right: -17px;
                    top: -6px;
                    background-color: #fff;
                    border-radius: 15px !important;
                  }
                  .noUi-handle:before, .noUi-handle:after {
                    height: 0px;
                  }
                  .noUi-connect{
                    background: #F41095 !important;
                  }
              `}
              </style>

              {videoDuration > 0 && (
                <Nouislider
                  range={{ min: 0, max: videoDuration }}
                  behaviour="smooth"
                  // step={1}
                  animate
                  start={[startTime, endTime]}
                  connect
                  onUpdate={handleSliderUpdate}
                  style={{ margin: "10px 10px" }}
                />
              )}
              {videoDuration === 0 && (
                <div className="text-red-500 text-sm mt-2">
                  Unable to load video properly. Please select a different file.
                </div>
              )}
              {maxDurationError && (
                <div className="text-red-500 text-sm mt-2">
                  {maxDurationError}
                </div>
              )}

              <div className="flex flex-col items-center justify-between gap-5 text-sm leading-[18.03px] font-normal text-[#666672] my-2">
                <div className="flex flex-col w-100 font-bold">
                  <span>Start Time: {startTime}</span>
                  <span>End Time: {endTime}</span>
                </div>
                <div className="flex items-center justify-end gap-[18px] mt-2">
                  <Button
                    className="w-25.5"
                    onClick={handleClose}
                    disabled={trimLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="w-auto"
                    variant="primary"
                    loading={trimLoading}
                    disabled={trimLoading}
                    onClick={handleTrim}
                  >
                    {trimLoading ? "Trimming..." : "Trim"}
                  </Button>
                </div>
              </div>
              {videoTrimmedUrl && (
                <video
                  src={videoTrimmedUrl}
                  controls
                  className="w-full max-w-xl rounded-lg mt-4"
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const VideoRangeSelector: React.FC<IFileDropZoneProps> = ({
  label,
  onSelectOrDrop,
  onClear,
  accept = "video/*",
  onChange,
  maxDuration = 10,
  name,
  rules,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setVideoFileValue(null);
    setVideoTrimmedUrl("");
    setVideoSrc("");
    setVideoDuration(0);
    setStartTime(0);
    setEndTime(0);
    if (
      fileInputRef.current instanceof HTMLInputElement &&
      fileInputRef.current.type === "file"
    ) {
      try {
        fileInputRef.current.value = "";
      } catch (err) {
        console.warn("Failed to reset file input:", err);
      }
    }
    onClear?.();
    setIsModalOpen(false);
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoDuration, setVideoDuration] = useState<number>(1);
  const [endTime, setEndTime] = useState<number>(0);
  const [startTime, setStartTime] = useState<number>(0);
  const [videoSrc, setVideoSrc] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);

  const [videoFileValue, setVideoFileValue] = useState<File | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [videoTrimmedUrl, setVideoTrimmedUrl] = useState<string>("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    field,
    formState: { errors },
  } = useController({ name: name || "", rules });
  const { ref, onChange: FieldOnChange, ...otherFieldValue } = field;

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    setDropError(null);

    const droppedFiles = e.dataTransfer.files;

    const acceptedFormats = accept.split(",").map((format) => format.trim());

    const videoFiles = Array.from(droppedFiles).filter((file) => {
      if (file.type.startsWith("video/")) {
        return true;
      }

      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      return acceptedFormats.some((format) => {
        if (format.startsWith(".")) {
          return format.toLowerCase() === `.${fileExtension}`;
        }
        if (format.includes("/")) {
          return file.type === format;
        }
        if (format.endsWith("/*")) {
          return file.type.startsWith(format.split("/*")[0]);
        }
        return false;
      });
    });

    if (videoFiles.length === 0) {
      setDropError(`Please drop a valid video file (${accept})`);
      return;
    }

    const file = videoFiles[0];
    setVideoFileValue(file);
    setVideoSrc(URL.createObjectURL(file));
    openModal();
    onSelectOrDrop?.(droppedFiles);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Reset states before loading new file
    setVideoDuration(0);
    setStartTime(0);
    setEndTime(0);
    setVideoTrimmedUrl("");
    setDropError(null);

    try {
      // Create object URL and verify the video
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");

      await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          if (isFinite(video.duration) && video.duration > 0) {
            resolve(true);
          } else {
            reject(new Error("Invalid video duration"));
          }
        };

        video.onerror = () => {
          reject(new Error("Failed to load video"));
        };

        video.src = url;

        // Timeout if metadata doesn't load
        setTimeout(() => {
          if (video.readyState === 0) {
            reject(new Error("Video loading timed out"));
          }
        }, 2000);
      });

      // If we get here, video is valid
      setVideoSrc(url);
      setVideoFileValue(file);
      openModal();
      onSelectOrDrop?.(files);
    } catch (error) {
      console.error("Error loading video:", error);
      setDropError(
        "The selected video file could not be loaded properly. Please choose a different file."
      );
      // URL.revokeObjectURL(url);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    let interval: NodeJS.Timeout;

    const checkDuration = () => {
      video.onloadedmetadata = () => {
        // const interval = setInterval(() => {
        //   const duration = video.duration;

        if (isFinite(video.duration) && video.duration > 1) {
          const roundedDuration = Math.floor(video.duration);

          setVideoDuration(roundedDuration);
          setEndTime(Math.min(roundedDuration, maxDuration));
          clearInterval(interval);
          // setDropError("");
        } else {
          setVideoDuration(0);
          setEndTime(0);
          setDropError(
            "The selected video file could not be loaded properly. Please choose a different file."
          );
          setVideoFileValue(null);
          setVideoSrc("");
          if (fileInputRef.current) fileInputRef.current.value = "";
        }

        // Stop polling after 1 second (1000ms) as a fallback
        // }, 200);

        // setTimeout(() => clearInterval(interval), 1000);
      };

      video.onerror = () => {
        clearInterval(interval);
        setVideoDuration(0);
      };

      interval = setInterval(() => {
        if (video.readyState > 0) {
          const duration = video.duration;
          if (isFinite(duration) && duration > 0) {
            const roundedDuration = Math.floor(duration);
            setVideoDuration(roundedDuration);
            setEndTime(Math.min(roundedDuration, maxDuration));
            clearInterval(interval);
          }
        }
      }, 200);

      setTimeout(() => {
        if (!isFinite(video.duration) || video.duration <= 0) {
          clearInterval(interval);
          setVideoDuration(0);
        }
      }, 2000);
    };
    checkDuration();

    return () => {
      clearInterval(interval);
      video.onloadedmetadata = null;
      video.onerror = null;
    };
  }, [videoSrc, maxDuration]);

  useEffect(() => {
    const loadFFmpeg = async () => {
      try {
        await ffmpeg.load();
        setIsScriptLoaded(true);
      } catch (error) {
        console.error("Failed to load FFmpeg:", error);
      }
    };
    loadFFmpeg();
  }, []);

  const handleClearFile = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setVideoFileValue(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setVideoTrimmedUrl("");
    setVideoSrc("");
    onClear?.();
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div>
      {label && (
        <label className="text-sm leading-[18.03px] font-normal text-[#666672] mt-2">
          {label}
        </label>
      )}
      {!videoSrc && (
        <>
          <div
            className={classNames(
              "w-full h-28 border-[0.86px] border-dashed rounded flex flex-col justify-center items-center text-xl text-gray-600 transition-colors cursor-pointer",
              label ? "mt-2.5" : ""
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClick}
          >
            <p className="text-sm leading-[18.03px] font-normal text-[#666672]">
              {isDragging ? (
                "Release to drop files"
              ) : videoFileValue ? (
                <div className="flex items-center gap-5">
                  <div>
                    {videoFileValue?.name},{" "}
                    {formatBytes(videoFileValue?.size || 0)}
                  </div>
                  <div onClick={handleClearFile}>
                    <CloseSvg className="text-[#1D1B20]" />
                  </div>
                </div>
              ) : (
                "Drag Files or Click to Browse"
              )}
            </p>
            <input
              ref={fileInputRef}
              className="hidden"
              type="file"
              accept={accept}
              onChange={handleFileUpload}
              {...otherFieldValue}
            />
          </div>

        </>
      )}
      {videoSrc && (
        <VideoTrimmer
          videoSrc={videoSrc}
          isOpen={isModalOpen}
          closeModal={closeModal}
          isScriptLoaded={isScriptLoaded}
          videoFileValue={videoFileValue}
          startTime={startTime}
          endTime={endTime}
          setStartTime={setStartTime}
          setEndTime={setEndTime}
          videoDuration={videoDuration}
          setVideoTrimmedUrl={setVideoTrimmedUrl}
          handleClearFile={handleClearFile}
          videoRef={videoRef}
          videoTrimmedUrl={videoTrimmedUrl}
          onChange={onChange}
          FieldOnChange={FieldOnChange}
          maxDuration={maxDuration}
        />
      )}

      <div className="text-sm leading-[18.03px] font-normal text-[#666672] mt-2" style={{
        display:'flex',
        justifyContent:'space-between'
      }}>
        <span>Accepts: mp4, x-m4v, mkv, mov, webm</span>
        <span>Max Video Duration: {maxDuration}</span>

      </div>

      {errors && name && (
        <span className="text-red-500 text-sm">
          {/* This line casts the error to FieldError and retrieves its message */}
          {(getNestedError(errors, name) as FieldError)?.message}
        </span>
      )}
      {dropError && (
        <div className="text-red-500 text-sm mt-2">{dropError}</div>
      )}
    </div>
  );
};

export default VideoRangeSelector;
